import { Effect } from 'effect';

/**
 * One step of the open document's undo history (W.4.132): the document as it
 * was before a tool's Apply replaced it, and the tool that replaced it.
 */
export interface StudioUndoStep {
  bytes: Uint8Array;
  name: string;
  /** Whether those bytes were unsaved. Undo restores the flag with them. */
  dirty: boolean;
  /** The tool whose Apply the step reverts, by its title ("Watermark"), or null for none. */
  tool: string | null;
}

/** Steps kept for one open document. */
export const STUDIO_UNDO_MAX_STEPS = 20;
/** Bytes the kept steps may hold together before the oldest go (the newest always stays). */
export const STUDIO_UNDO_MAX_BYTES = 200 * 1024 * 1024;
/** Why Undo waits while a tool holds staged edits, when the tool does not say. */
export const STUDIO_STAGED_UNDO_REASON = 'Apply or close the tool first';

/** What replaced the open document, as the history sees it. */
export type StudioUndoChange =
  /** A tool's change to the open document; `previous` is the document it replaced. */
  | { kind: 'edit'; previous: StudioUndoStep }
  /** Undo restored `step`, which leaves the history. */
  | { kind: 'undo'; step: StudioUndoStep }
  /** Another document opened, or the open one closed: the history starts again. */
  | { kind: 'open' };

/**
 * The history after a change to the open document. An edit puts the document
 * it replaced on top, keeps at most 20 steps and drops the oldest while the
 * steps hold more than about 200 MB together; the newest step stays, so one
 * Undo always works. An undo takes off the step it restored, and another
 * document empties the history.
 */
export function nextStudioUndoHistoryEffect(
  steps: readonly StudioUndoStep[],
  change: StudioUndoChange,
): Effect.Effect<StudioUndoStep[]> {
  return Effect.sync(() => {
    if (change.kind === 'open') return [];
    if (change.kind === 'undo') {
      const index = steps.lastIndexOf(change.step);
      return index < 0 ? [...steps] : steps.filter((_, at) => at !== index);
    }
    const next = [...steps, change.previous].slice(-STUDIO_UNDO_MAX_STEPS);
    let bytes = next.reduce((sum, step) => sum + step.bytes.byteLength, 0);
    while (next.length > 1 && bytes > STUDIO_UNDO_MAX_BYTES) {
      bytes -= next.shift()!.bytes.byteLength;
    }
    return next;
  });
}

export function nextStudioUndoHistory(
  steps: readonly StudioUndoStep[],
  change: StudioUndoChange,
): StudioUndoStep[] {
  return Effect.runSync(nextStudioUndoHistoryEffect(steps, change));
}

/** What one Undo does now. */
export type StudioUndoPlan =
  | { kind: 'nothing' }
  /** A tool holds staged edits on the pages the step would replace; `reason` says what to do. */
  | { kind: 'blocked'; reason: string }
  /** Open the step's bytes as an edit, unsaved only if they were. */
  | { kind: 'restore'; step: StudioUndoStep; open: { dirty: boolean; edit: true } };

/** What Undo does with this history while the active tool holds `staged` work (its reason) or none. */
export function planStudioUndoEffect(
  steps: readonly StudioUndoStep[],
  staged: string | null,
): Effect.Effect<StudioUndoPlan> {
  return Effect.sync(() => {
    const step = steps.at(-1);
    if (!step) return { kind: 'nothing' };
    if (staged !== null) return { kind: 'blocked', reason: staged };
    return { kind: 'restore', step, open: { dirty: step.dirty, edit: true } };
  });
}

export function planStudioUndo(
  steps: readonly StudioUndoStep[],
  staged: string | null,
): StudioUndoPlan {
  return Effect.runSync(planStudioUndoEffect(steps, staged));
}

/** The Undo control's name for the step on top ("Undo Watermark"), or null with nothing to undo. */
export function studioUndoLabel(step: StudioUndoStep | undefined): string | null {
  if (!step) return null;
  return step.tool ? `Undo ${step.tool}` : 'Undo the last change';
}

/** The notice after an undo: what it undid. */
export function studioUndoneNotice(step: StudioUndoStep): string {
  return step.tool ? `Undid ${step.tool}` : 'Undid the last change';
}

/** A key event, as the shortcut reads it. */
export interface StudioUndoKey {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  target: EventTarget | null;
}

/** True on an Apple platform, where the shortcut is ⌘Z rather than Ctrl+Z. */
export function isApplePlatform(platform: string): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(platform);
}

/** The `aria-keyshortcuts` value of the Undo control. */
export function studioUndoKeyShortcut(apple: boolean): string {
  return apple ? 'Meta+Z' : 'Control+Z';
}

/**
 * Focus in a place that has its own text undo: a field, a list or editable
 * text. ⌘Z there stays the browser's.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  const element = target as { tagName?: unknown; isContentEditable?: unknown } | null;
  if (!element || typeof element.tagName !== 'string') return false;
  if (element.isContentEditable === true) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName.toUpperCase());
}

/**
 * ⌘Z on an Apple platform and Ctrl+Z elsewhere, with no other modifier, and
 * focus outside text entry. Shift+⌘Z (redo) is not it.
 */
export function isStudioUndoShortcutEffect(
  event: StudioUndoKey,
  apple: boolean,
): Effect.Effect<boolean> {
  return Effect.sync(() => {
    if (event.key.toLowerCase() !== 'z' || event.shiftKey || event.altKey) return false;
    const modifier = apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
    return modifier && !isTextEntryTarget(event.target);
  });
}

export function isStudioUndoShortcut(event: StudioUndoKey, apple: boolean): boolean {
  return Effect.runSync(isStudioUndoShortcutEffect(event, apple));
}
